import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as doorsApi from "../../api/doors.api.js";
import { Tile } from "../../components/Tile.js";
import {
  ClockIcon,
  ControllerIcon,
  DoorIcon,
  OverrideIcon,
  ShieldIcon,
  WarningIcon,
} from "../../components/Icon.js";
import { useAuth } from "../../lib/auth-context.js";
import { formatAge, formatTimestamp, sweepFindingLabel } from "../../lib/format.js";
import { WATCHDOG_POLL_MS, refetchIntervalFor } from "../../lib/polling.js";

export function WatchdogPage() {
  const { can } = useAuth();
  const queryClient = useQueryClient();

  const watchdog = useQuery({
    queryKey: ["watchdog"],
    queryFn: doorsApi.watchdogStatus,
    refetchInterval: refetchIntervalFor(WATCHDOG_POLL_MS),
  });

  const sweep = useMutation({
    mutationFn: doorsApi.runSweep,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["watchdog"] });
      void queryClient.invalidateQueries({ queryKey: ["doors"] });
    },
  });

  const data = watchdog.data;
  const lastRunAt = data?.lastRun?.startedAt ?? null;

  // "Running" with zero sweeps for an hour is a failure that looks like health. The age of the
  // last sweep is the number that tells the difference.
  const intervalMs = (data?.intervalSeconds ?? 120) * 1000;
  const overdue =
    data?.enabled === true &&
    lastRunAt !== null &&
    Date.now() - new Date(lastRunAt).getTime() > intervalMs * 3;
  const neverSwept = data?.enabled === true && lastRunAt === null;

  return (
    <>
      <div className="page-head">
        <h1 id="page-heading" tabIndex={-1}>
          Watchdog
        </h1>
        {can("manage_security") ? (
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => sweep.mutate()}
            disabled={sweep.isPending || data?.inFlight}
          >
            {sweep.isPending || data?.inFlight ? "Sweeping…" : "Run a sweep now"}
          </button>
        ) : null}
      </div>

      <p className="page-intro">
        The watchdog reads every enabled door on a schedule, so a stuck door is found in minutes
        rather than when someone happens to try their finger on it. It never reverts an override a
        person authorised.
      </p>

      {!data?.enabled ? (
        <div className="banner banner-warning">
          <span className="banner-icon" aria-hidden="true">
            ⚠
          </span>
          <div className="banner-body">
            <strong>The watchdog is disabled by configuration.</strong> Nothing is checking for
            doors that drift into an override on their own.
          </div>
        </div>
      ) : null}

      {overdue || neverSwept ? (
        <div className="banner banner-critical" role="alert">
          <span className="banner-icon" aria-hidden="true">
            ✗
          </span>
          <div className="banner-body">
            <strong>The watchdog is marked running but is not sweeping.</strong>{" "}
            {neverSwept
              ? "It has not completed a single sweep."
              : `The last sweep was ${formatAge(lastRunAt)}, well past the ${data?.intervalSeconds}s interval.`}{" "}
            Treat this as "not watching", not as healthy.
          </div>
        </div>
      ) : null}

      <div className="tiles">
        <Tile
          label="State"
          value={data?.running ? "running" : "stopped"}
          small
          icon={<ShieldIcon />}
        />
        <Tile
          label="Last sweep"
          value={formatAge(lastRunAt)}
          small
          note={formatTimestamp(lastRunAt)}
          icon={<ClockIcon />}
        />
        <Tile
          label="Interval"
          value={`${data?.intervalSeconds ?? "—"}s`}
          small
          icon={<ClockIcon />}
        />
        <Tile label="Sweeps" value={String(data?.totals.sweeps ?? 0)} icon={<ShieldIcon />} />
        <Tile
          label="Doors checked"
          value={String(data?.totals.doorsChecked ?? 0)}
          icon={<DoorIcon />}
        />
        <Tile
          label="Unexpected overrides"
          value={String(data?.totals.unexpectedOverrides ?? 0)}
          tone={(data?.totals.unexpectedOverrides ?? 0) > 0 ? "critical" : undefined}
          icon={<OverrideIcon />}
        />
        <Tile
          label="Restores attempted"
          value={String(data?.totals.restoresAttempted ?? 0)}
          note={`${data?.totals.restoresSucceeded ?? 0} succeeded`}
          tone={
            (data?.totals.restoresAttempted ?? 0) > (data?.totals.restoresSucceeded ?? 0)
              ? "warning"
              : undefined
          }
          icon={<ControllerIcon />}
        />
        <Tile
          label="Read failures"
          value={String(data?.totals.readFailures ?? 0)}
          tone={(data?.totals.readFailures ?? 0) > 0 ? "warning" : undefined}
          icon={<WarningIcon />}
        />
      </div>

      {data?.lastRun?.lastError ? (
        <div className="banner banner-critical">
          <span className="banner-icon" aria-hidden="true">
            ✗
          </span>
          <div className="banner-body">
            <strong>Last error:</strong> {data.lastRun.lastError}
          </div>
        </div>
      ) : null}

      <h2>Findings from the last sweep</h2>
      <div className="panel">
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th scope="col">Door</th>
                <th scope="col">Finding</th>
                <th scope="col">Detail</th>
              </tr>
            </thead>
            <tbody>
              {(data?.lastSweep?.findings ?? []).map((finding, index) => {
                const label = sweepFindingLabel(finding.finding);
                return (
                  <tr key={`${finding.doorId}-${index}`}>
                    <td>
                      <div className="cell-name">{finding.name}</div>
                      <div className="cell-sub mono">{finding.doorId}</div>
                    </td>
                    <td>
                      <span className={`status tone-${label.tone}`}>
                        <span className="status-icon" aria-hidden="true">
                          {label.icon}
                        </span>
                        <span>{label.word}</span>
                      </span>
                    </td>
                    <td>{finding.detail}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {!data?.lastSweep || data.lastSweep.findings.length === 0 ? (
          <div className="empty">
            No findings recorded yet. Run a sweep to populate this, or wait for the next one.
          </div>
        ) : null}
      </div>
    </>
  );
}
